import type { CSSProperties } from 'react';
import type { ThemeContextValue } from '@course-authoring/shared-types';

type ThemeTokens = ThemeContextValue['tokens'];

export function createCourseEditorPageStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    page: {
      height: '100%',
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      overflow: 'auto',
    },
    pagePreview: {
      overflow: 'hidden',
    },
    topBar: {
      position: 'sticky',
      top: 0,
      zIndex: 20,
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.sm,
      flexWrap: 'wrap',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      border: 'none',
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      padding: `${tokens.spacing.sm} ${tokens.spacing.sm}`,
    },
    titleInput: {
      minWidth: '240px',
      flex: '1 1 320px',
      border: 'none',
      backgroundColor: 'transparent',
      color: tokens.text.primary,
      fontSize: '22px',
      fontWeight: 700,
      outline: 'none',
      padding: `${tokens.spacing.xs} 0`,
    },
    courseId: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: '13px',
      color: tokens.text.muted,
      whiteSpace: 'nowrap',
    },
    saveStatus: {
      color: tokens.text.muted,
      fontSize: '13px',
      whiteSpace: 'nowrap',
    },
    saveStatusError: {
      color: tokens.ui.errorText,
    },
    topActions: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginLeft: 'auto',
      flexWrap: 'wrap',
    },
    secondaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    disabledButton: {
      opacity: 0.55,
      cursor: 'not-allowed',
    },
    content: {
      display: 'grid',
      gridTemplateColumns: 'minmax(360px, 45%) minmax(420px, 1fr)',
      gap: tokens.spacing.xl,
      padding: tokens.spacing.xl,
      alignItems: 'start',
      boxSizing: 'border-box',
    },
    previewPane: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      overflow: 'hidden',
    },
    leftColumn: {
      position: 'sticky',
      top: tokens.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      minWidth: 0,
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    card: {
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.sm,
      overflow: 'hidden',
    },
    cardHeader: {
      padding: `${tokens.spacing.lg} ${tokens.spacing.lg} ${tokens.spacing.sm}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    cardTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 700,
      color: tokens.text.primary,
    },
    cardSubtitle: {
      margin: `${tokens.spacing.xs} 0 0`,
      fontSize: '13px',
      color: tokens.text.muted,
    },
    cardBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      padding: tokens.spacing.lg,
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: '13px',
      fontWeight: 700,
      color: tokens.text.primary,
    },
    input: {
      width: '100%',
      height: '40px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      minHeight: '112px',
      padding: tokens.spacing.md,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      lineHeight: 1.5,
      resize: 'vertical',
      outline: 'none',
      boxSizing: 'border-box',
    },
    inlineRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(180px, 1fr) minmax(160px, 1fr)',
      gap: tokens.spacing.md,
      alignItems: 'end',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 600,
      minHeight: '40px',
    },
    thumbnailButton: {
      width: '100%',
      minHeight: '128px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px dashed ${tokens.borders.interactive}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      cursor: 'pointer',
      overflow: 'hidden',
      padding: tokens.spacing.md,
      boxSizing: 'border-box',
    },
    thumbnailImage: {
      maxWidth: '100%',
      maxHeight: '180px',
      objectFit: 'cover',
      borderRadius: tokens.radius.sm,
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
    },
    toggleButton: {
      width: '46px',
      height: '26px',
      border: 'none',
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.brand.primary,
      padding: '3px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    toggleButtonOff: {
      backgroundColor: tokens.borders.interactive,
      justifyContent: 'flex-start',
    },
    toggleKnob: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.sm,
    },
    placeholderBox: {
      border: `1px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: tokens.spacing.md,
      color: tokens.text.muted,
      fontSize: '14px',
      backgroundColor: tokens.backgrounds.surface,
    },
    detailGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: tokens.spacing.md,
    },
    detailLabel: {
      color: tokens.text.muted,
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      marginBottom: tokens.spacing.xs,
    },
    detailValue: {
      color: tokens.text.primary,
      fontSize: '14px',
      lineHeight: 1.4,
    },
    monospace: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    },
    lessonHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    lessonList: {
      display: 'flex',
      flexDirection: 'column',
      padding: tokens.spacing.md,
    },
    lessonRow: {
      display: 'grid',
      // Trailing column is 'auto' (not a fixed width) so the open-link and
      // remove/Re-enable controls hug each other and size to their own
      // content instead of leaving dead space around whichever is narrower.
      gridTemplateColumns: '32px 36px minmax(0, 1fr) 88px 120px auto',
      alignItems: 'center',
      gap: tokens.spacing.md,
      padding: tokens.spacing.md,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.sm,
    },
    lessonRowActions: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
    },
    lessonRowDragging: {
      opacity: 0.65,
    },
    dragHandle: {
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      cursor: 'grab',
    },
    positionBadge: {
      width: '28px',
      height: '28px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: tokens.radius.full,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      fontSize: '13px',
      fontWeight: 700,
    },
    lessonTitle: {
      color: tokens.text.primary,
      fontSize: '15px',
      fontWeight: 700,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    lessonMeta: {
      marginTop: tokens.spacing.xs,
      color: tokens.text.muted,
      fontSize: '12px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    iconButton: {
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.muted,
      cursor: 'pointer',
      fontSize: '16px',
    },
    dangerIconButton: {
      color: tokens.ui.errorText,
    },
    // Compact one-line Re-enable button on suppressed client-context rows.
    reEnableButton: {
      height: '32px',
      padding: `0 ${tokens.spacing.sm}`,
      whiteSpace: 'nowrap',
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    // Armed state of the two-click remove confirm (mirrors the lesson editor's
    // ContentBlockWrapper delete button).
    dangerIconButtonConfirm: {
      backgroundColor: tokens.ui.errorText,
      color: tokens.text.onInverse,
      border: `1px solid ${tokens.ui.errorText}`,
    },
    insertControl: {
      minHeight: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    insertButton: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.brand.primary,
      fontSize: '13px',
      fontWeight: 700,
      cursor: 'pointer',
      boxShadow: tokens.shadow.sm,
    },
    addLessonFooter: {
      display: 'flex',
      justifyContent: 'center',
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg} ${tokens.spacing.xl}`,
    },
    primaryButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      border: 'none',
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
      fontSize: '14px',
      fontWeight: 700,
      cursor: 'pointer',
      boxShadow: tokens.shadow.sm,
    },
    emptyState: {
      padding: tokens.spacing["2xl"],
      textAlign: 'center',
      color: tokens.text.muted,
      fontSize: '14px',
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: tokens.spacing.lg,
      boxSizing: 'border-box',
    },
    modal: {
      width: '640px',
      maxWidth: '100%',
      maxHeight: '88vh',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '12px',
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.md,
      overflow: 'hidden',
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: tokens.spacing.lg,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    modalTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 700,
    },
    modalBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      overflow: 'auto',
    },
    addLessonModalBody: {
      gap: 0,
      padding: 0,
    },
    modalSearchToolbar: {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
      flexWrap: 'wrap',
      padding: tokens.spacing.lg,
      borderBottom: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
    },
    modalSearchInput: {
      width: '320px',
      maxWidth: '100%',
      height: '38px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      flex: '0 1 320px',
    },
    modalResultsCount: {
      color: tokens.text.muted,
      fontSize: '13px',
      whiteSpace: 'nowrap',
    },
    refreshLessonsButton: {
      height: '38px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      marginLeft: 'auto',
      padding: `0 ${tokens.spacing.md}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    modalLessonContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
    },
    modalFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      borderTop: `1px solid ${tokens.borders.default}`,
    },
    modalCloseButton: {
      border: 'none',
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      cursor: 'pointer',
      fontSize: '22px',
      lineHeight: 1,
    },
    librarySectionLabel: {
      color: tokens.text.muted,
      fontSize: '12px',
      fontWeight: 800,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      margin: `${tokens.spacing.sm} 0 ${tokens.spacing.xs}`,
    },
    libraryRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) 110px 96px',
      alignItems: 'center',
      gap: tokens.spacing.md,
      padding: `${tokens.spacing.md} 0`,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    standardTitleLine: {
      marginTop: tokens.spacing.xs,
      color: tokens.text.muted,
      fontSize: '12px',
      lineHeight: 1.4,
    },
    libraryRowDescription: {
      marginTop: tokens.spacing.xs,
      color: tokens.text.muted,
      fontSize: '13px',
      lineHeight: 1.4,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    libraryRowDisabled: {
      opacity: 0.45,
    },
    smallButton: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.brand.primary}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.brand.primary,
      fontSize: '13px',
      fontWeight: 700,
      cursor: 'pointer',
    },
    mutedText: {
      color: tokens.text.muted,
      fontSize: '13px',
    },
    confirmText: {
      margin: 0,
      color: tokens.text.muted,
      fontSize: '14px',
      lineHeight: 1.5,
    },
  };
}
